import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Table } from 'antd'
import {
    DeleteOutlined,
    EditOutlined,
    GroupOutlined,
    Loading3QuartersOutlined,
    ShoppingOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { useDataViewReducer } from '@pushly/aqe/lib/components/data-view/reducer'
import { DataView } from '@pushly/aqe/lib/components/data-view/data-view'
import { DataViewContext } from '@pushly/aqe/lib/components/data-view/context'
import { DataSourceResponse } from '@pushly/aqe/lib/components/data-view/types'
import { AppService } from '../../../services'
import { ItemGroupsService } from '../../../services/item-groups.service'
import { ItemGroupResponse } from './types'
import { CatalogItemType } from '../types'
import { useService } from '@pushly/aqe/lib/hooks'
import { stripUndefined } from '../../../_utils/strip-undefined'
import dayjs from 'dayjs'
import {
    AntdTableEmptyPlaceholder,
    Button,
    emptyStates,
    NoneValue,
    SectionEmptyState,
} from '@pushly/aqe/lib/components'
import { capitalizeFirstLetter } from 'src/_utils/string'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getItemTypeLabel(itemType?: string): string {
    switch (itemType) {
        case CatalogItemType.PRODUCT:
            return 'Products'
        case CatalogItemType.TICKETED_EVENT:
            return 'Events'
        case CatalogItemType.RECIPE:
            return 'Recipes'
        default:
            return 'Items'
    }
}

function formatDate(dateStr: string): string {
    const date = dayjs(dateStr)
    return date.isValid() ? date.format('LLL') : '—'
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ItemGroupsDataViewProps {
    domainId?: number
    itemType?: string
}

export const ItemGroupsDataView: React.FC<ItemGroupsDataViewProps> = ({ domainId, itemType }) => {
    const appService = useService(AppService)
    const itemGroupsService = useService(ItemGroupsService)

    const [hasError, setHasError] = useState(false)
    const [showArchiveModal, setShowArchiveModal] = useState(false)
    const [selectedGroup, setSelectedGroup] = useState<{ id: number; name: string } | null>(null)
    const [itemCounts, setItemCounts] = useState<Record<number, number | 'loading' | 'error'>>({})

    // Prefetch fields into singleton cache so create/edit pages render instantly
    useEffect(() => {
        if (domainId) {
            itemGroupsService.getFieldsCached(domainId)
        }
    }, [domainId, itemGroupsService])

    const [context, dispatch, requestDataSourceReload] = useDataViewReducer<ItemGroupResponse>({
        id: 'item-groups',
        levelResource: {
            level: 'domain',
            id: domainId!,
        },
        defaultFilters: {},
    })

    const dataSourceLoader = useCallback(
        async (
            filters: Record<string, any>,
            tableState: { pagination: { current?: number; pageSize?: number } },
        ): Promise<DataSourceResponse<ItemGroupResponse>> => {
            const response = await itemGroupsService.list(domainId!, {
                showLoadingScreen: false,
                cancellationKey: 'item-groups-list.fetch',
                query: stripUndefined({
                    page: tableState.pagination.current,
                    limit: tableState.pagination.pageSize,
                    search: filters.search ?? undefined,
                }),
            })

            if (response.cancelled) {
                return { ok: false, data: [], cancelled: true }
            }

            if (!response.ok) {
                setHasError(true)
                return { ok: false, data: [] }
            }

            setHasError(false)
            return {
                ok: true,
                data: response.data ?? [],
                meta: response.meta,
            }
        },
        [domainId, itemGroupsService],
    )

    // Fetch counts for all loaded item groups
    useEffect(() => {
        const items = context.dataSource ?? []
        if (!domainId || items.length === 0) return

        const uncounted = items.filter((item) => !(item.id in itemCounts))
        if (uncounted.length === 0) return

        setItemCounts((prev) => {
            const next = { ...prev }
            uncounted.forEach((item) => {
                next[item.id] = 'loading'
            })
            return next
        })

        uncounted.forEach((item) => {
            itemGroupsService
                .count(domainId, item.filters)
                .then((res) => {
                    setItemCounts((prev) => ({
                        ...prev,
                        [item.id]: res.ok && res.data?.count != null ? res.data.count : 'error',
                    }))
                })
                .catch(() => {
                    setItemCounts((prev) => ({ ...prev, [item.id]: 'error' }))
                })
        })
    }, [context.dataSource, domainId, itemGroupsService])

    const handleArchiveClick = useCallback((group: ItemGroupResponse) => {
        setSelectedGroup({ id: group.id, name: group.name })
        setShowArchiveModal(true)
    }, [])

    const handleArchive = useCallback(async () => {
        if (selectedGroup && domainId) {
            await itemGroupsService.archive(domainId, selectedGroup.id)
            setShowArchiveModal(false)
            setSelectedGroup(null)
            requestDataSourceReload()
        }
    }, [selectedGroup, domainId, itemGroupsService, requestDataSourceReload])

    const handleDismissArchiveModal = useCallback(() => {
        setShowArchiveModal(false)
        setSelectedGroup(null)
    }, [])

    const handleRetry = useCallback(() => {
        setHasError(false)
        requestDataSourceReload()
    }, [requestDataSourceReload])

    const itemTypeDisplay = capitalizeFirstLetter(`${itemType} Group`)

    if (!domainId) {
        return null
    }

    return (
        <>
            <DataViewContext.Provider value={{ state: context, dispatch }}>
                <DataView<ItemGroupResponse>
                    dataSourceLoader={dataSourceLoader}
                    hideAutoRefreshOptions={true}
                    className="aqe-table-well"
                    filters={[
                        {
                            key: 'search',
                            type: 'search',
                            componentProps: {
                                placeholder: 'Search by name',
                                style: { width: 300 },
                                // The no-results copy tells the user to clear the search, so the control has
                                // to live on the input: the empty state's Clear-filters button can reset
                                // the filter value but not the term the filter holds in local state.
                                allowClear: true,
                            },
                        },
                    ]}
                    table={{
                        rowKey: (row) => row.id,
                        // Table chrome follows the load, not the row count. A load that succeeded still
                        // has real columns and real pagination links even when it returned zero rows, so
                        // the header row and the pager hold through both empty states; a failed load has
                        // neither, so both drop while the failure notice fills the table body.
                        showHeader: !hasError,
                        hideFooter: hasError,
                        noData: hasError
                            ? emptyStates.error({
                                  text: `Unable to load ${itemTypeDisplay}s`,
                                  description:
                                      'Please try again. If the problem persists, contact your account manager.',
                                  onRetry: handleRetry,
                              })
                            : (ctx) =>
                                  !ctx.hasActiveFilters ? (
                                      <AntdTableEmptyPlaceholder
                                          iconVariant="chip"
                                          icon={<GroupOutlined />}
                                          text={`No ${itemTypeDisplay}s yet`}
                                          description="Group items using filters tailored to your catalog's configuration. Membership stays current automatically as your items change."
                                          action={
                                              <Button
                                                  type="primary"
                                                  onClick={(_e) => {
                                                      appService.routeWithinDomain('catalogs/item-groups/new')
                                                  }}
                                              >
                                                  Create {itemTypeDisplay}
                                              </Button>
                                          }
                                      />
                                  ) : (
                                      emptyStates.noResults({
                                          text: `No ${itemTypeDisplay}s match your search`,
                                          description: `Clear search to see all ${itemTypeDisplay}s.`,
                                          icon: <GroupOutlined />,
                                      })
                                  ),
                    }}
                >
                    <Table.Column<ItemGroupResponse> key="name" dataIndex="name" title="Name" ellipsis={true} />

                    <Table.Column<ItemGroupResponse>
                        key="itemCount"
                        title={`Number of ${getItemTypeLabel(itemType)}`}
                        width={160}
                        render={(_, record) => {
                            const count = itemCounts[record.id]
                            if (count === undefined || count === 'loading')
                                return <Loading3QuartersOutlined spin={true} />
                            if (count === 'error') return <NoneValue />
                            return count.toLocaleString()
                        }}
                    />

                    <Table.Column<ItemGroupResponse>
                        key="updatedAt"
                        dataIndex="updated_at"
                        title="Last Updated"
                        width={180}
                        render={(val: string) => formatDate(val)}
                    />

                    <Table.Column<ItemGroupResponse>
                        key="createdAt"
                        dataIndex="created_at"
                        title="Created"
                        width={180}
                        render={(val: string) => formatDate(val)}
                    />

                    <Table.Column<ItemGroupResponse>
                        key="actions"
                        title="Actions"
                        align="right"
                        width={120}
                        render={(_, record) => (
                            <Button
                                actions={[
                                    {
                                        divider: false,
                                        key: 'archive',
                                        text: 'Archive',
                                        icon: <DeleteOutlined aria-hidden="true" />,
                                        danger: true,
                                        onClick: (e) => {
                                            e.domEvent.stopPropagation()
                                            handleArchiveClick(record)
                                        },
                                    },
                                ]}
                                size="small"
                                icon={<EditOutlined aria-hidden="true" />}
                                onClick={(_e) => {
                                    appService.routeWithinDomain(`catalogs/item-groups/${record.id}/edit`, false, {
                                        itemGroup: record,
                                    })
                                }}
                            >
                                Edit
                            </Button>
                        )}
                    />
                </DataView>
            </DataViewContext.Provider>

            <Modal
                open={showArchiveModal}
                title="Archive Item Group"
                okText="Archive"
                okButtonProps={{
                    variant: 'solid',
                    color: 'danger',
                }}
                onOk={handleArchive}
                onCancel={handleDismissArchiveModal}
                destroyOnHidden={true}
            >
                <p>
                    Are you sure you want to archive <strong>{selectedGroup?.name}</strong>?
                </p>
                <p>This action cannot be undone.</p>
            </Modal>
        </>
    )
}
